import { cloudApiClient } from '@/services/clientSideCloudApiService';
import { OptimizationStatus } from '@/types/rapidModel';
import { withInstrumentedServerAction } from '@/utils';

interface RawModelJobsApiResponse {
  data: RawModelJobsApiResponseItem[];
}

interface RawModelJobsApiResponseItem {
  id: number;
  name: string;
  optimization_status: OptimizationStatus;
  job_status: {
    id: number;
    job_id: number | null;
    type: string;
    queue: string | null;
    attempts: number;
    progress_now: number;
    progress_max: number;
    status: string;
    input: string | null;
    output: string | null;
    created_at: string;
    updated_at: string;
    started_at: string | null;
    finished_at: string | null;
    key: string;
  } | null;
  progress: number;
  processing_step: string;
  external: boolean;
  rawmodel: {
    id: number;
    name: string;
  };
  preset: {
    id: number;
    name: string;
  } | null;
}

export type PendingOutput = {
  workflowName?: string;
  progress: number;
  rawModelId: number;
  outputId: number;
  optimisationStatus: OptimizationStatus;
  startedAt?: string;
  name: string;
  workflowId?: number;
};

export const getRawModelPendingOutputs = async function (): Promise<PendingOutput[]> {
  const { data: jobs } = await cloudApiClient.get<RawModelJobsApiResponse>('rawmodel/jobs');

  return jobs.data.map((i) => ({
    workflowName: i.preset?.name,
    progress: getProgress(i),
    rawModelId: i.rawmodel.id,
    outputId: i.id,
    optimisationStatus: i.optimization_status,
    startedAt: i.job_status?.created_at,
    name: i.name,
    isPBR: false,
    workflowId: i.preset?.id,
  }));
};

const getProgress = (job: RawModelJobsApiResponseItem): number => {
  if (!job) {
    return 0;
  }

  const progress = job.job_status?.progress_now ?? 0;
  const status = job.processing_step;

  // Map of statuses to their respective order in the workflow
  const statusMap = new Map<string, number>([
    ['Fetching raw model from storage ...', 1],
    ['Analysing raw model ...', 2],
    ['Optimizing ...', 3],
    ['Preparing export formats ...', 4],
    ['Generating animated thumbnail ...', 5],
    ['Publishing results ...', 6],
  ]);

  // Determine the progress multiplier from the map
  const stepMultiplier = statusMap.get(status);

  // Return the progress based on the multiplier, or the raw progress if no specific status matches
  return stepMultiplier ? getPercentage(stepMultiplier, progress) : progress;
};

const getPercentage = (step: number, progress: number, total = 6) => {
  const base = 100 / total;
  return base * (step - 1) + base * (progress / 100);
};
